import React from "react";
import {
  Box,
  InputLabel,
  Typography,
} from "@mui/material";
import { StyledTextField } from "@components/CustomInput/customInput";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import { setSearchCoordinates } from "@gis/redux/gisSlice";

const MapSearchInput = () => {

  const dispatch = useDispatch<AppDispatch>();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchList, setSearchList] = React.useState<any[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setSearchList([]);
    }
  }

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounce effect for search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length >= 3) {
        handlePlaceSearch(searchTerm);
      } else {
        setSearchList([]);
      }
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handlePlaceSearch = async (searchTerm: string) => {
    try {
      const encodedSearch = encodeURIComponent(searchTerm);
      const response = await fetch(
        `${process.env.REACT_APP_API_OPENSTREET_URL}/search?format=json&q=${encodedSearch}&countrycodes=in&addressdetails=1&limit=10`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'NHAI-GIS-App/1.0'
          }
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: any[] = await response.json();
      if (data && data.length > 0) {
        setSearchList(data);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  return (
    <Box sx={{ position: 'relative' }} ref={containerRef}>
      <InputLabel htmlFor="location_search" sx={{ textWrap: "wrap", mb: 1 }}>
        Search address or coordinates (lat, long) to locate your Ammenity/ Utility:
      </InputLabel>
      <StyledTextField
        id="location_search"
        sx={{ width: "100%", mb: 1 }}
        name="location_search"
        type="text"
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Enter Ammenity/ Utility Address"
        fullWidth
      />
      {searchList.length > 0 && (
        <>
          <Box sx={{
            position: "absolute",
            background: "#fff",
            zIndex: 1000,
            maxHeight: "30vh",
            minWidth: "30rem",
            overflow: "scroll",
            border: "1px solid #ccc",
            padding: 1,
            top: '5.2rem',
            borderRadius: '4px'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Search Results:</Typography>
            {searchList.map((item, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2"
                  sx={{
                    mb: 1,
                    cursor: "pointer",
                    fontSize: "14px",
                    borderRadius: "3px",
                    padding: "4px",
                    transition: "background 0.2s",
                    "&:hover": {
                      background: "#f0f0ff"
                    }
                  }}
                  onClick={() => {
                    dispatch(setSearchCoordinates({ latitude: Number(item.lat), longitude: Number(item.lon) })); 
                    setSearchList([]);
                  }}>{item.display_name}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default MapSearchInput;